/**
 * Add A2A Agent Modal (SWRM-134)
 * Modal for selecting and publishing a pipeline as an A2A agent
 */

import React, { useCallback, useEffect, useState } from 'react';
import { X, Search, CheckCircle, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/api';
import { Pipeline } from '../../types/pipeline';
import { formatApiError } from '../../utils/errorFormatter';
import { useNotificationStore } from '../../store';

interface AddA2AAgentModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const AddA2AAgentModal: React.FC<AddA2AAgentModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const { addNotification } = useNotificationStore();

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [filteredPipelines, setFilteredPipelines] = useState<Pipeline[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(
    null
  );
  const [customSlug, setCustomSlug] = useState('');

  const loadPipelines = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getPipelines();
      // Filter only ACTIVE pipelines that are NOT already published
      const availablePipelines = data.filter(
        (p) => p.status === 'ACTIVE' && !p.is_a2a_enabled
      );
      setPipelines(availablePipelines);
      setFilteredPipelines(availablePipelines);
    } catch (error: any) {
      console.error('Error loading pipelines:', error);
      addNotification(
        `Failed to load pipelines: ${formatApiError(error)}`,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    loadPipelines();
  }, [loadPipelines]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      setFilteredPipelines(
        pipelines.filter(
          (p) =>
            p.name.toLowerCase().includes(query) ||
            p.description?.toLowerCase().includes(query)
        )
      );
    } else {
      setFilteredPipelines(pipelines);
    }
  }, [searchQuery, pipelines]);

  const handlePipelineSelect = (pipeline: Pipeline) => {
    setSelectedPipeline(pipeline);
    // Auto-generate slug suggestion from pipeline name
    const slug = pipeline.name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    setCustomSlug(slug);
  };

  const handlePublish = async () => {
    if (!selectedPipeline) return;

    try {
      setIsPublishing(true);
      await apiService.publishA2AAgent(selectedPipeline.id, customSlug || null);
      onSuccess();
    } catch (error: any) {
      console.error('Error publishing agent:', error);
      addNotification(
        `Failed to publish agent: ${formatApiError(error)}`,
        'error'
      );
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Publish Agent
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!selectedPipeline ? (
            <>
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search pipelines..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Pipeline List */}
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">
                    Loading pipelines...
                  </span>
                </div>
              ) : filteredPipelines.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 dark:text-gray-400">
                    {searchQuery
                      ? 'No pipelines match your search'
                      : 'No unpublished pipelines available'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                    All active pipelines are either already published or there
                    are no active pipelines.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredPipelines.map((pipeline) => (
                    <button
                      key={pipeline.id}
                      onClick={() => handlePipelineSelect(pipeline)}
                      className="w-full text-left p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {pipeline.name}
                          </h3>
                          {pipeline.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {pipeline.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-gray-500 dark:text-gray-500">
                              v{pipeline.version}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-500">
                              {pipeline.workflow_count} workflows
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Selected Pipeline */}
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {selectedPipeline.name}
                    </h3>
                    {selectedPipeline.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {selectedPipeline.description}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedPipeline(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Slug Configuration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Agent Slug (URL identifier)
                </label>
                <input
                  type="text"
                  placeholder="my-agent-slug"
                  value={customSlug}
                  onChange={(e) => setCustomSlug(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  This will be used in the agent card URL. Leave empty to
                  auto-generate from pipeline name.
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  Example:{' '}
                  <code className="bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded">
                    /.well-known/agents/{customSlug || 'slug'}.json
                  </code>
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={isPublishing}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          {selectedPipeline && (
            <button
              onClick={handlePublish}
              disabled={isPublishing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isPublishing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Publish Agent
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

